import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Divider,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
} from '@mui/material';
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Upload as UploadIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { settingsApi, authApi } from '../services/api';
import { useApp } from '../App';

const Settings = () => {
  const { showNotification, user } = useApp();
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({
    storageType: 'json',
    bloombergMock: true,
    version: '1.0.0',
  });
  const [health, setHealth] = useState(null);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const [settingsRes, healthRes] = await Promise.all([
        settingsApi.get(),
        settingsApi.health(),
      ]);
      setSettings(settingsRes.data);
      setHealth(healthRes.data);
    } catch (error) {
      showNotification('Ошибка загрузки настроек', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      showNotification('Пароли не совпадают', 'error');
      return;
    }

    try {
      await authApi.changePassword(passwordForm.currentPassword, passwordForm.newPassword);
      showNotification('Пароль изменен', 'success');
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      showNotification(error.response?.data?.error || 'Ошибка смены пароля', 'error');
    }
  };

  const handleExportData = async () => {
    showNotification('Экспорт данных начат...', 'info');
    // Implementation would create a backup file
  };

  const handleImportData = async () => {
    showNotification('Функция импорта данных', 'info');
    // Implementation would restore from backup
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" fontWeight={600}>
          Настройки
        </Typography>
        <Button startIcon={<RefreshIcon />} onClick={fetchSettings}>
          Обновить
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* System Info */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Информация о системе
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>Версия</TableCell>
                    <TableCell align="right">{settings.version}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Тип хранилища</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={settings.storageType === 'json' ? 'JSON' : 'SQLite'}
                        size="small"
                        color={settings.storageType === 'json' ? 'info' : 'primary'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Bloomberg</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={settings.bloombergMock ? 'Mock' : 'Live'}
                        size="small"
                        color={settings.bloombergMock ? 'warning' : 'success'}
                      />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Статус сервера</TableCell>
                    <TableCell align="right">
                      <Chip
                        label={health?.status === 'ok' ? 'Online' : 'Offline'}
                        size="small"
                        color={health?.status === 'ok' ? 'success' : 'error'}
                      />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </Grid>

        {/* User Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Профиль пользователя
              </Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell>Имя пользователя</TableCell>
                    <TableCell align="right">{user?.username}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell>Роль</TableCell>
                    <TableCell align="right">
                      <Chip label={user?.role} size="small" />
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle1" gutterBottom>
                Смена пароля
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField
                  size="small"
                  type="password"
                  label="Текущий пароль"
                  value={passwordForm.currentPassword}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, currentPassword: e.target.value })
                  }
                />
                <TextField
                  size="small"
                  type="password"
                  label="Новый пароль"
                  value={passwordForm.newPassword}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, newPassword: e.target.value })
                  }
                />
                <TextField
                  size="small"
                  type="password"
                  label="Подтверждение пароля"
                  value={passwordForm.confirmPassword}
                  onChange={(e) =>
                    setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })
                  }
                />
                <Button
                  variant="contained"
                  onClick={handleChangePassword}
                  disabled={
                    !passwordForm.currentPassword ||
                    !passwordForm.newPassword ||
                    !passwordForm.confirmPassword
                  }
                >
                  Сменить пароль
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Data Management */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Управление данными
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Используйте эти функции для резервного копирования и восстановления данных
              </Alert>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<DownloadIcon />}
                  onClick={handleExportData}
                >
                  Экспорт данных
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={handleImportData}
                >
                  Импорт данных
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Storage Settings */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Настройки хранилища
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Изменение типа хранилища требует перезапуска сервера
              </Alert>
              <FormControlLabel
                control={
                  <Switch
                    checked={settings.storageType === 'json'}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        storageType: e.target.checked ? 'json' : 'sqlite',
                      })
                    }
                  />
                }
                label="Использовать JSON хранилище"
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                JSON хранилище проще в использовании, SQLite обеспечивает лучшую производительность
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* API Settings */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Настройки интеграций
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Bloomberg API Key"
                    type="password"
                    placeholder="Введите API ключ Bloomberg"
                    helperText="Оставьте пустым для использования mock данных"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Telegram Bot Token"
                    type="password"
                    placeholder="Введите токен Telegram бота"
                    helperText="Для уведомлений об операциях"
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button variant="contained" startIcon={<SaveIcon />}>
                  Сохранить настройки
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Settings;
